import * as React from 'react'
import { LinkToRedirect } from '..'
import { CompareCredit } from '../../../types/compare-credit'
import { CardArt } from '../card-art'
import Link from 'next/link'
import { autoScroll, supify } from '../../utils'
import { clickRatesAndFees } from '../../clients/segment'
import { ApplyNowIcon } from '../link-to-redirect/ApplyNowIcon'
import { CardRecommendedCredit } from '../card-recommended-credit'
import { StarRatingEditor } from '../star-rating-editor'

/**
 * 2026.06 - This component will be used on a landing page test for Social for
 * GRO-925. If it doesn't work out we'll need to delete it
 */

function shouldShowRatesAndFees(card: CompareCredit.FormattedCard) {
  return (
    card._type === 'card' && card.ratesAndFees && card.ratesAndFees !== 'N/A'
  )
}

export const SummaryRowGro925: React.FC<{
  card: CompareCredit.FormattedCard
  categoryId: string
  externalId: string
  dataId: string
  iconWidth?: string
  orderId: string
  productHighlight: string
  ribbonText?: string
  ribbonColor?: string
}> = (props) => {
  const {
    card,
    categoryId,
    externalId,
    dataId,
    orderId,
    productHighlight,
    ribbonColor,
    ribbonText,
  } = props

  // Scroll to section
  const handleClickProduct1 = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    //const dataId = event.target.dataset.id
    const dataId = event.currentTarget.getAttribute('data-id')
    if (dataId) {
      const productSection = document.getElementById(dataId)
      if (productSection) {
        autoScroll(productSection, -48)
      }
    }
  }

  return (
    <div
      className={`c-summary__row / w-full / border-b border-slate-200 ${
        ribbonText ? 'pt-2 pb-3' : 'py-2'
      }`}
    >
      {ribbonText ? (
        <div className="w-full">
          <div
            className={`inline-block / mb-2 / leading-snug text-fs13 font-bold ${
              ribbonColor === 'tetriary'
                ? 'c-ribbon-bg-rt pl-3 pr-6 text-primary'
                : null
            }
        ${
          ribbonColor === 'blue'
            ? 'c-ribbon-bg-rt c-ribbon-bg-rt--primary-bright pl-3 pr-6 text-white'
            : null
        }
        ${ribbonColor === 'none' ? 'w-full mb-2 text-primary-bright' : null}
        `}
          >
            {ribbonText}
          </div>
        </div>
      ) : null}

      <div className="flex flex-row">
        <div className="c-summary__cell / flex flex-col items-center / flex-shrink-0 / w-1/5 sm:w-[20%] md:w-[18%]">
          <div className="c-card-art / w-full / mb-1.5">
            <LinkToRedirect
              externalId={externalId}
              linkParams={card.applyNowLinkParameters}
              orderId={orderId}
              query={undefined}
              slug={card.slug}
            >
              <div className="relative flex rounded-sm overflow-hidden / fs_card-tile_card-art">
                <span className="c-card-art__content / absolute / flex flex-wrap content-center items-center justify-center / z-10 left-0 / w-full h-full / leading-4 text-center text-white text-xs font-bold">
                  <span className="w-4 h-4 mb-0.5">
                    <ApplyNowIcon
                      className="inline-block"
                      width={16}
                      height={16}
                      aria-hidden={true}
                    />
                  </span>
                  <span className="w-full font-semibold">Apply Now</span>
                </span>

                <CardArt
                  cardArt={card.cardArt}
                  imgClasses="relative / z-0 / w-full h-full object-cover top-0"
                  customCodeSnippets={card.customCodeSnippets}
                  issuer={card.issuer.slug.current}
                  name={card.name}
                  orderId={orderId}
                  slug={card.slug}
                  type={card._type}
                  categoryId={categoryId}
                  externalId={externalId}
                  _rev={null}
                />
              </div>
            </LinkToRedirect>
          </div>

          <button
            className="flex-shrink-0 inline-block / mb-1 / leading-[1.25] text-slate-500 font-semibold text-center text-[12px] no-underline text-left / 
            hover:text-primary-bright"
            onClick={handleClickProduct1}
            data-id={dataId}
          >
            Jump to Details
          </button>
        </div>
        <div className="c-summary__cell / relative / flex-grow / w-4/5 / pl-4 sm:px-5">
          <span className="absolute -top-[.15rem] right-[.25rem] / hidden sm:block">
            <ArrowUpIconSvg />
          </span>
          <p className="text-primary leading-5 md:text-lg">
            <strong>{productHighlight}</strong>
          </p>
          <p className="mb-0.5 leading-snug">
            <LinkToRedirect
              externalId={externalId}
              linkParams={card.applyNowLinkParameters}
              orderId={orderId}
              query={undefined}
              slug={card.slug}
            >
              <div
                className="inline-block / w-full / text-slate-500 leading-snug text-fs13 / hover:text-primary-bright cursor-pointer / transition-all"
                dangerouslySetInnerHTML={{
                  __html: supify(card.name),
                }}
              />
            </LinkToRedirect>
          </p>
          <div>
            <LinkToRedirect
              externalId={externalId}
              linkParams={card.applyNowLinkParameters}
              orderId={orderId}
              query={undefined}
              slug={card.slug}
            >
              <div className="flex flex-row items-center justify-center / w-full / py-1.5 / text-sm text-white font-bold text-center / bg-green-cta rounded-sm / hover:bg-green-cta-light / sm:hidden">
                Apply&nbsp;Now
                <span className="w-4 h-4 / ml-1">
                  <ApplyNowIcon className="lazyload" width={16} height={16} />
                </span>
              </div>
            </LinkToRedirect>
            {shouldShowRatesAndFees(card) ? (
              <p className="w-full / mt-1 / text-primary-bright-dark text-xs text-center / sm:hidden / hover:text-primary-bright">
                <Link
                  href={card.ratesAndFees}
                  onClick={() => {
                    clickRatesAndFees({
                      applyNowLink: card.applyNowLink,
                      component: 'Tips Summarey Summary Row',
                      name: card.name,
                      url: window.location.href,
                    })
                  }}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Rates &amp; Fees
                </Link>
              </p>
            ) : (
              <p
                className="w-full / mt-1 / text-primary-bright-dark text-xs text-center / sm:hidden"
                aria-hidden="true"
              >
                &nbsp;
              </p>
            )}
          </div>

          <div className="flex flex-row">
            <div className="relative z-10 / w-[50%] sm:hidden">
              <div className="-mt-1 -ml-6 scale-[.7]">
                <StarRatingEditor
                  productSlug={card.slug}
                  stars={card.expertReviewRating}
                />
              </div>
            </div>
            <div className="w-[50%] sm:w-full">
              <CardRecommendedCredit
                card={card}
                disclosureStyling={`t-align-rt`}
                iconWidth={'w-7'}
                iconPadding={'pl-[0.5rem]'}
              />
            </div>
            <span
              className={
                shouldShowRatesAndFees(card)
                  ? 'absolute top-[40%] -left-[2%] / sm:hidden'
                  : 'absolute top-[50%] -left-[2%] / sm:hidden'
              }
            >
              <ArrowRightIconSvg />
            </span>
          </div>
        </div>
        <div className="c-summary__cell / sm:shrink-0 sm:w-[30%] lg:w-[28%] / hidden sm:block">
          <LinkToRedirect
            externalId={externalId}
            linkParams={card.applyNowLinkParameters}
            orderId={orderId}
            query={undefined}
            slug={card.slug}
          >
            <div className="flex items-center justify-center / w-full / px-1 py-2 / text-sm text-white font-bold text-center / bg-green-cta rounded-sm / hover:bg-green-cta-light">
              Apply Now
              <span className="w-4 h-4 / ml-1.5">
                <ApplyNowIcon width={16} height={16} className="lazyload" />
              </span>
            </div>
          </LinkToRedirect>
          {card._type === 'card' &&
            card.ratesAndFees &&
            card.ratesAndFees !== 'N/A' && (
              <p className="w-full / pt-1 / text-primary-bright-dark text-xs w-full text-center / hover:text-primary-bright">
                <Link
                  href={card.ratesAndFees}
                  onClick={() => {
                    clickRatesAndFees({
                      applyNowLink: card.applyNowLink,
                      component: 'Tips Summarey Summary Row Details',
                      name: card.name,
                      url: window.location.href,
                    })
                  }}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Rates &amp; Fees
                </Link>
              </p>
            )}
          <div className="relative z-10 / w-full / mt-1 / hidden sm:block">
            <div className="w-full / pl-1 / scale-[.8] lg:scale-[.9]">
              <StarRatingEditor
                productSlug={card.slug}
                stars={card.expertReviewRating}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const ArrowUpIconSvg: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="34"
    height="11"
    viewBox="0 0 34 11"
    fill="none"
  >
    <path
      d="M1.99026 8.37158C1.5089 8.64234 0.899185 8.47162 0.628423 7.99026C0.357661 7.5089 0.528383 6.89919 1.00974 6.62843L1.99026 8.37158ZM33.9182 9.88787L22.3968 10.6568L27.4916 0.294503L33.9182 9.88787ZM1.5 7.5L1.00974 6.62843C7.38315 3.04341 16.991 1.93037 26.1575 4.96824L25.8429 5.91747L25.5283 6.8667C16.8497 3.99053 7.83169 5.0858 1.99026 8.37158L1.5 7.5Z"
      fill="#F6B300"
    />
  </svg>
)

const ArrowRightIconSvg: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="46"
    viewBox="0 0 16 46"
    fill="none"
  >
    <path
      d="M14.8364 -5.92964e-07L3.29495 0.357359L9.37517 10.1739L14.8364 -5.92964e-07ZM14.6706 45.9861C15.2152 46.0778 15.731 45.7105 15.8226 45.1659C15.9142 44.6212 15.5469 44.1055 15.0023 44.0139L14.6706 45.9861ZM7.18557 4.73883L6.43526 4.07774C1.16541 10.0588 -0.644692 19.8335 0.696874 28.3572C2.03319 36.8475 6.61083 44.6305 14.6706 45.9861L14.8364 45L15.0023 44.0139C8.22072 42.8732 3.95854 36.2168 2.67255 28.0462C1.39181 19.909 3.17693 10.8012 7.93588 5.39991L7.18557 4.73883Z"
      fill="#F6B300"
    />
  </svg>
)
